import ADDRESS_METADATA from '@/constants/address-metadata.json';



/*
  Accessor for the per-country address metadata sourced from Google's
  chromium-i18n service (the same data Chrome autofill uses).

  The raw JSON is a flat shape with tilde-delimited string lists and
  optional fields. This module normalizes one country at a time into an
  object the rest of the app consumes — labels, required-field flags,
  state options, postal-code regex, and a renderer that consumes the
  country's `fmt` string.

  Defaults (for countries with sparse metadata) come from Google's
  libaddressinput specification:
    fmt:     '%N%n%O%n%A%n%D%n%C, %S %Z'
    require: 'AC'  (address + city required, state/zip/sublocality optional)
*/

const DEFAULT_FMT = '%N%n%O%n%A%n%D%n%C, %S %Z';
const DEFAULT_REQUIRE = 'AC';

const ZIP_LABELS = {
  zip: 'ZIP Code',
  pin: 'PIN Code',
  eircode: 'Eircode',
  postal: 'Postal Code'
};

const STATE_LABELS = {
  state: 'State',
  county: 'County',
  emirate: 'Emirate',
  prefecture: 'Prefecture',
  island: 'Island',
  province: 'Province',
  parish: 'Parish',
  area: 'Area',
  department: 'Department',
  oblast: 'Oblast',
  do_si: 'Do/Si'
};

const CITY_LABELS = {
  suburb: 'Suburb',
  district: 'District',
  post_town: 'Post Town',
  city: 'City'
};

const SUBLOCALITY_LABELS = {
  neighborhood: 'Neighborhood',
  district: 'District',
  village_township: 'Village / Township',
  townland: 'Townland',
  suburb: 'Suburb'
};

const DEFAULT_LABELS = {
  zip: 'Postal Code',
  state: 'Province',
  city: 'City',
  sublocality: 'Suburb'
};

const FORMAT_TOKENS = ['N', 'O', 'A', 'D', 'C', 'S', 'Z', 'X'];

const ARTICLES = new Set(['and', 'of', 'the', 'a', 'an', 'or']);

const metaCache = new Map();



function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}



function labelFor(type, dictionary, defaultLabel) {
  if (!type) return defaultLabel;
  if (dictionary[type]) return dictionary[type];
  return capitalize(type.replace(/_/g, ' '));
}



function splitTilde(value) {
  return value ? value.split('~') : [];
}



function buildPostalRegex(zipPattern) {
  if (!zipPattern) return null;
  try {
    return new RegExp(`^(?:${zipPattern})$`);
  }
  catch (_) {
    return null;
  }
}



/**
 * Title-case a SCREAMING country name and strip parenthetical disambiguators.
 *
 * "UNITED STATES" → "United States"
 * "BOSNIA AND HERZEGOVINA" → "Bosnia and Herzegovina"
 * "CONGO (DEM. REP.)" → "Congo"
 *
 * Used as a fallback when Intl.DisplayNames can't resolve the ISO code (rare).
 */
function titleCaseCountryName(name) {
  if (!name) return '';
  const stripped = name.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  return stripped
    .toLowerCase()
    .split(' ')
    .map((word, i) => {
      if (i > 0 && ARTICLES.has(word)) return word;
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}



/**
 * Return a normalized metadata object for a country code.
 *
 * Unknown codes return a default-shaped object so callers don't have to
 * null-check (the form just renders the most-permissive set of fields).
 *
 * @param {string} code - ISO 3166-1 alpha-2 (case-insensitive)
 * @returns {object}
 */
function getCountryMeta(code) {
  const upper = code ? String(code).toUpperCase() : '';

  if (metaCache.has(upper)) {
    return metaCache.get(upper);
  }

  const raw = ADDRESS_METADATA[upper] || {};
  const fmt = raw.fmt || DEFAULT_FMT;
  const lfmt = raw.lfmt || fmt;
  const require = raw.require || DEFAULT_REQUIRE;

  const subKeys = splitTilde(raw.sub_keys);
  const subNames = splitTilde(raw.sub_names);
  const subLnames = splitTilde(raw.sub_lnames);

  const states = subKeys.length
    ? subKeys.map((key, i) => ({
      key,
      name: subNames[i] || key,
      latinName: subLnames[i] || subNames[i] || key
    }))
    : null;

  const meta = {
    code: upper,
    rawName: raw.name || upper,
    fmt,
    lfmt,
    require: {
      address: require.includes('A'),
      city: require.includes('C'),
      state: require.includes('S'),
      zip: require.includes('Z'),
      sublocality: require.includes('D')
    },
    labels: {
      zip: labelFor(raw.zip_name_type, ZIP_LABELS, DEFAULT_LABELS.zip),
      state: labelFor(raw.state_name_type, STATE_LABELS, DEFAULT_LABELS.state),
      city: labelFor(raw.locality_name_type, CITY_LABELS, DEFAULT_LABELS.city),
      sublocality: labelFor(raw.sublocality_name_type, SUBLOCALITY_LABELS, DEFAULT_LABELS.sublocality)
    },
    postalCodeRegex: buildPostalRegex(raw.zip),
    postalCodeExample: raw.zipex ? raw.zipex.split(',')[0] : '',
    states,
    upper: raw.upper || ''
  };

  metaCache.set(upper, meta);
  return meta;
}



/**
 * Return the localized display name for a country.
 *
 * Uses Intl.DisplayNames when available; falls back to a title-cased
 * version of the raw chromium name (which is SCREAMING and sometimes
 * carries parenthetical disambiguators).
 *
 * @param {string} code - ISO 3166-1 alpha-2
 * @param {string} [locale='en-US']
 * @returns {string}
 */
function getCountryName(code, locale = 'en-US') {
  const upper = code ? String(code).toUpperCase() : '';
  if (!upper) return '';

  try {
    const displayNames = new Intl.DisplayNames(locale, { type: 'region', fallback: 'none' });
    const name = displayNames.of(upper);
    if (name) return name;
  }
  catch (_) {
    /* fall through */
  }

  const raw = ADDRESS_METADATA[upper];
  return raw ? titleCaseCountryName(raw.name) : upper;
}



/**
 * Return a sorted list of `{ code, name }` for use in a country dropdown.
 *
 * @param {object} [options]
 * @param {string[]} [options.countries] - Restrict to this allowlist (e.g.
 *   merchant's supported countries). Unknown codes are filtered out.
 *   When omitted, every country in the metadata is returned.
 * @param {string} [options.locale='en-US']
 * @returns {{ code: string, name: string }[]}
 */
function getCountryOptions({ countries, locale = 'en-US' } = {}) {
  const allCodes = Object.keys(ADDRESS_METADATA);
  const codes = countries && countries.length
    ? countries.map(c => String(c).toUpperCase()).filter(c => ADDRESS_METADATA[c])
    : allCodes;

  return codes
    .map(code => ({ code, name: getCountryName(code, locale) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}



function cleanLine(line) {
  return line
    .replace(/\s+/g, ' ')
    .replace(/(?:^|\s)[,/-](?=\s|$)/g, ' ')
    .replace(/,\s*,/g, ',')
    .replace(/^[\s,/-]+|[\s,/-]+$/g, '')
    .trim();
}



/**
 * Render an address into an array of lines using the country's format string.
 *
 * Empty fields are removed and surrounding punctuation/whitespace is cleaned
 * up — so a US address with no state still renders sensibly.
 *
 * Address keys consumed: name, organization (or org), address (or street),
 * sublocality, city, state, zip, sortingCode.
 *
 * @param {object} address
 * @param {string} countryCode - ISO 3166-1 alpha-2
 * @param {object} [options]
 * @param {boolean} [options.latin=false] - Use the latinized format (`lfmt`)
 *   for countries that publish one. Useful when displaying a foreign-script
 *   address in an English-script context.
 * @returns {string[]}
 */
function formatAddress(address, countryCode, { latin = false } = {}) {
  if (!address) return [];

  const meta = getCountryMeta(countryCode);
  const template = latin ? meta.lfmt : meta.fmt;

  let stateValue = address.state || '';
  if (latin && meta.states && stateValue) {
    const match = meta.states.find(s => s.key === stateValue || s.name === stateValue);
    if (match) stateValue = match.latinName;
  }

  /*
    Our address shape historically splits the street into `line1` and `line2`
    (apartment / suite). Chromium models `%A` as a single street block; join
    the two with a comma when both are present so they render together.
  */
  const street = address.address || address.street
    || [address.line1, address.line2].filter(Boolean).join(', ')
    || '';

  const tokenValues = {
    N: address.name || address.addressee || '',
    O: address.organization || address.org || '',
    A: street,
    D: address.sublocality || '',
    C: address.city || '',
    S: stateValue,
    Z: address.zip || address.postalCode || '',
    X: address.sortingCode || ''
  };

  const output = FORMAT_TOKENS.reduce(
    (acc, token) => acc.split(`%${token}`).join(tokenValues[token]),
    template
  );

  return output
    .split('%n')
    .map(cleanLine)
    .filter(line => line.length > 0);
}



/**
 * Convenience wrapper around `formatAddress` that joins the country-aware
 * lines with a separator — for inline contexts (single-line summaries,
 * directions buttons) where multi-line layout isn't an option.
 *
 * @param {object} address
 * @param {string} countryCode
 * @param {object} [options]
 * @param {string} [options.separator=', '] - String to place between lines
 * @param {boolean} [options.latin=false]
 * @returns {string}
 */
function formatAddressInline(address, countryCode, { separator = ', ', latin = false } = {}) {
  return formatAddress(address, countryCode, { latin }).join(separator);
}



export {
  getCountryMeta,
  getCountryName,
  getCountryOptions,
  formatAddress,
  formatAddressInline,
  titleCaseCountryName
};
